/**
 * back-button.ts — 統一 ‹ 返回 鈕(給「不是用 pageShell 畫殼」的畫面用)。
 *
 * pageShell 已內建統一 ‹ 返回 + 返回堆疊註冊;但有些畫面(academy.ts / settings.ts /
 * chapter-detail.ts)自己手刻 `.ef-page` 外框,沒走 pageShell。本 helper 讓它們也能用
 * 同一顆 ‹ 返回 鈕 + 同一套返回堆疊(iOS 風左緣滑動共用),不必各畫各的。
 *
 * 做兩件事:
 *   1. `pushBackHandler(onBack, { confirm })` 註冊進集中式返回堆疊(左緣滑動 / goBack 共用)。
 *   2. 在 `mountInto` 容器最前面插一顆 `.ef-back-btn`(點擊 = `goBack()`)。
 *
 * 回傳 `unmount()`:取消註冊 + 移除鈕(畫面換頁時呼;走 main.ts setMode 的畫面其實會被
 * resetBackStack,但仍建議在 in-place 換頁時呼以保持乾淨)。
 *
 * ⚠️ 純導覽,不碰任何學習/遊戲數值。
 */

import { pushBackHandler, goBack } from './back-nav';

/** mountBackButton 設定。 */
export interface BackButtonOptions {
  /** 返回處理器(回上一頁)。 */
  onBack: () => void;
  /** 選填確認字串(破壞性畫面);有值 → goBack 先跳確認,取消則不返回。 */
  confirm?: string;
  /** 鈕插入位置:'prepend'(預設,插到容器最前)或 'append'。 */
  position?: 'prepend' | 'append';
}

/**
 * 在 `mountInto` 容器內掛統一 ‹ 返回 鈕,並把 `onBack` 註冊進返回堆疊。
 * @returns `unmount()` —— 取消註冊 + 移除鈕。
 */
export function mountBackButton(mountInto: HTMLElement, opts: BackButtonOptions): () => void {
  const off = pushBackHandler(opts.onBack, { confirm: opts.confirm });

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ef-back-btn';
  btn.setAttribute('data-testid', 'page-back');
  btn.setAttribute('aria-label', '返回');
  btn.innerHTML =
    '<span class="ef-back-btn__chevron" aria-hidden="true">‹</span><span>返回</span>';
  btn.addEventListener('click', () => { goBack(); });

  if (opts.position === 'append') {
    mountInto.appendChild(btn);
  } else {
    mountInto.insertBefore(btn, mountInto.firstChild);
  }

  let unmounted = false;
  return () => {
    if (unmounted) return;
    unmounted = true;
    off();
    btn.remove();
  };
}
